import { io } from 'socket.io-client';
import { getTokenAuth } from '../utils/authUtils';

/**
 * Service pour gérer la connexion Socket.io
 */
class SocketService {
    constructor() {
        this.socket = null;
        this.listeners = new Map();
        this.connected = false;
        this._debugListenersAttached = false;
    }

    _serializeSocketError(error) {
        if (!error) return { error: null };
        return {
            message: error.message,
            description: error.description,
            type: error.type,
            context: error.context,
            data: error.data,
            stack: error.stack
        };
    }

    _attachDebugListeners() {
        if (!this.socket || this._debugListenersAttached) return;

        this._debugListenersAttached = true;

        // Erreurs runtime côté socket
        this.socket.on('error', (error) => {
            console.error('[SocketService] socket error:', this._serializeSocketError(error));
        });

        // Logs de reconnexion (events du Manager)
        const manager = this.socket.io;
        if (manager && typeof manager.on === 'function') {
            manager.on('reconnect_attempt', (attempt) => {
                console.warn('[SocketService] reconnect_attempt:', { attempt });
            });
            manager.on('reconnect_error', (error) => {
                console.error('[SocketService] reconnect_error:', this._serializeSocketError(error));
            });
            manager.on('reconnect_failed', () => {
                console.error('[SocketService] reconnect_failed');
            });
            manager.on('reconnect', (attempt) => {
                console.log('[SocketService] reconnected:', { attempt });
            });
        }
    }

    _computeSocketUrl() {
        const rawUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

        // REACT_APP_API_URL est souvent configurée comme base REST (ex: http://host:5000/api/v1).
        // Socket.io, lui, écoute sur l'origin (http://host:5000) avec path '/socket.io'.
        try {
            // URL(...) permet de récupérer proprement l'origin même si rawUrl contient un path.
            const origin = new URL(rawUrl, window.location.origin).origin;
            return { rawUrl, socketUrl: origin };
        } catch (_) {
            // Fallback regex si URL() échoue
            const socketUrl = String(rawUrl)
                .replace(/\/(api\/v\d+)(\/)?$/i, '')
                .replace(/\/$/, '');
            return { rawUrl, socketUrl };
        }
    }

    /**
     * Initialiser la connexion Socket.io
     * @returns {Promise<boolean>} Succès de la connexion
     */
    async connect() {
        return new Promise((resolve, reject) => {
            try {
                if (this.socket && this.connected) {
                    console.log('Socket already connected');
                    resolve(true);
                    return;
                }

                const token = getTokenAuth();
                if (!token) {
                    console.error('[SocketService] No authentication token found (required for Socket.io auth)');
                    reject(new Error('Authentication required'));
                    return;
                }

                const { rawUrl, socketUrl } = this._computeSocketUrl();
                const options = {
                    auth: { token },
                    transports: ['websocket', 'polling'],
                    path: '/socket.io',
                    reconnection: true,
                    reconnectionAttempts: 5,
                    reconnectionDelay: 1000
                };

                // Ne jamais logger le token lui-même.
                console.log('[SocketService] Connecting…', {
                    rawUrl,
                    socketUrl,
                    path: options.path,
                    transports: options.transports,
                    hasToken: Boolean(token),
                    tokenLength: typeof token === 'string' ? token.length : null
                });

                // Créer une nouvelle connexion Socket.io
                this.socket = io(socketUrl, options);
                this._debugListenersAttached = false;
                this._attachDebugListeners();

                // Événement de connexion réussie
                this.socket.on('connect', () => {
                    console.log('[SocketService] Socket.io connected', {
                        id: this.socket?.id,
                        transport: this.socket?.io?.engine?.transport?.name
                    });
                    this.connected = true;
                    resolve(true);
                });

                // Événement d'erreur de connexion
                this.socket.on('connect_error', (error) => {
                    console.error('[SocketService] Socket.io connect_error:', this._serializeSocketError(error));
                    this.connected = false;
                    reject(error);
                });

                // Événement de déconnexion
                this.socket.on('disconnect', (reason) => {
                    console.warn('[SocketService] Socket.io disconnected:', {
                        reason,
                        wasConnected: this.connected
                    });
                    this.connected = false;
                });

                // Réactiver les écouteurs d'événements précédents
                if (this.listeners.size > 0) {
                    this.listeners.forEach((callback, event) => {
                        this.socket.on(event, callback);
                    });
                }
            } catch (error) {
                console.error('Error initializing socket connection:', error);
                this.connected = false;
                reject(error);
            }
        });
    }

    /**
     * Déconnecter Socket.io
     */
    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
            this.connected = false;
            this._debugListenersAttached = false;
            console.log('Socket.io disconnected');
        }
    }

    /**
     * Ajouter un écouteur d'événement
     * @param {string} event - Nom de l'événement
     * @param {Function} callback - Fonction de rappel
     */
    on(event, callback) {
        if (!this.socket) {
            console.warn('Socket not initialized. Event will be registered after connection.');
            this.listeners.set(event, callback);
            return;
        }

        this.socket.on(event, callback);
        this.listeners.set(event, callback);
    }

    /**
     * Supprimer un écouteur d'événement
     * @param {string} event - Nom de l'événement
     */
    off(event) {
        if (!this.socket) {
            console.warn('Socket not initialized');
            this.listeners.delete(event);
            return;
        }

        this.socket.off(event);
        this.listeners.delete(event);
    }

    /**
     * Émettre un événement
     * @param {string} event - Nom de l'événement
     * @param {*} data - Données à envoyer
     */
    emit(event, data) {
        if (!this.socket || !this.connected) {
            console.error('Socket not connected');
            return;
        }

        this.socket.emit(event, data);
    }

    /**
     * Vérifier si la connexion est active
     * @returns {boolean} État de la connexion
     */
    isConnected() {
        return this.connected && this.socket && this.socket.connected;
    }
}

// Exporter une instance unique du service
const socketService = new SocketService();
export default socketService;